/*
 * @descrption
 * Questa classe astratta fornisce un'interfaccia generica per la gestione delle richieste HTTP
 * utilizzando Axios. È progettata per essere estesa da altre classi che implementano specifiche
 * logiche di comunicazione con API remote.
 * Funzionalità principali:
 *   - Gestione dei metodi HTTP comuni (GET, POST, PUT, PATCH, DELETE).
 *   - Supporto per la configurazione degli header personalizzati.
 *   - Gestione degli errori con una classe personalizzata (`ApiError`).
 *   - Possibilità di annullare richieste in corso con `abortLast()`.
 *   - Formattazione automatica dei parametri nelle richieste GET.
 *   - Definizione di un `HttpMethod` enum per garantire coerenza nei metodi HTTP supportati.
 */

import axios, { AxiosInstance } from "axios";
import config from "../../config";
import { logger } from "../utils/logger";

type Params = Record<string, any>;

// Classe per gestire errori API con dettagli specifici.
class ApiError extends Error {
    statusCode?: number;
    details?: any;
    isCancelled?: boolean;
    // §417: distingue un timeout (la richiesta è lenta, ma il server potrebbe aver
    // comunque terminato) da un vero errore, per poter dare un messaggio diverso
    // invece del generico "errore nel salvataggio" — vedi TaskAddForm.tsx.
    isTimeout?: boolean;

    constructor(message: string, statusCode?: number, details?: any, isCancelled?: boolean, isTimeout?: boolean) {
        super(message);
        this.statusCode = statusCode;
        this.details = details;
        this.isCancelled = isCancelled;
        this.isTimeout = isTimeout;
    }
}

// Enumerazione dei metodi HTTP supportati.
enum HttpMethod {
    GET = "GET",
    POST = "POST",
    DELETE = "DELETE",
    PUT = "PUT",
    PATCH = "PATCH"
}

/* Classe astratta per la gestione delle richieste HTTP.
   Deve essere estesa da classi concrete che definiscono gli endpoint specfici.
*/
abstract class AbstractCommunicationController {
    protected readonly SESSION_HEADER_NAME: string = "ApolloSession";

    private controller: AxiosInstance;
    private signalController?: AbortController;

    constructor(url: string) {
        this.controller = axios.create({
            baseURL: url,
            timeout: 20000,
            withCredentials: config.NODE_ENV === "production",
            headers: { "Content-Type": "application/json" }
        });
    }

    /* Metodo per ottenere gli header personalizzati per ogni richiesta.
       Le classi derivate possono sovrascriverlo per aggiungere autenticazione, token ecc.
       Di default, allega il sid della sessione attiva (v. getSessionHeader()).
    */
    protected getHeaders(_endpoint: string): Record<string, string> {
        return this.getSessionHeader();
    }

    // Legge il sid della sessione attiva da `localStorage` e lo restituisce come header.
    // Centralizzato qui perché era duplicato identico in più controller derivati (§7.2).
    protected getSessionHeader(): Record<string, string> {
        const sessionString = localStorage.getItem(config.LOCAL_STORAGE_SESSION_KEY);
        const sessionObjs = sessionString ? JSON.parse(sessionString) : [];
        const session = sessionObjs.find((obj: any) => obj.active)?.sid ?? "";

        return { [this.SESSION_HEADER_NAME]: session };
    }

    /*Metodo generico per effettuare richieste HTTP */
    // Metodi pubblici per effettuare richieste HTTP

    private async baseCall<T = any>(
        method: HttpMethod,
        endpoint: string,
        data: Params = {},
        customHeaders: Record<string, string> = {}, // Aggiungi questo parametro
        timeout?: number
    ): Promise<T> {
        logger.debug(`API Request: ${method} ${endpoint}`, data);

        this.signalController = new AbortController();

        // Combina gli header di base con quelli specifici della richiesta
        const headers = {
            ...this.getHeaders(endpoint),
            ...customHeaders
        };

        const config = {
            headers,
            signal: this.signalController.signal,
            timeout: timeout
        };

        try {
            const fn = this.getFunctionByHttpMethod(method);
            let res;

            // Differenzazione dei metodi HTTP
            if (method === HttpMethod.GET) res = await fn(endpoint + this.formatGetData(data), config);
            else if (method === HttpMethod.DELETE) res = await fn(endpoint, { ...config, data });
            else res = await fn(endpoint, data, config);

            logger.debug(`API Response: ${method} ${endpoint}`, res.data);
            return res.data;
        } catch (err) {
            if (axios.isAxiosError(err)) {
                if (axios.isCancel(err)) logger.log(`Cancelled: ${method} ${endpoint}`);
                else logger.error(`API Error: ${method} ${endpoint}`, err);
                if (err.response?.status === 401) {
                    logger.log("Errore 401, logout");
                    localStorage.removeItem("ApolloSession"); // Rimuove la sessione salvata
                    window.dispatchEvent(new CustomEvent("userLoggedOut"));
                }
                throw new ApiError(
                    err.response?.data?.message || `Errore ${method} su ${endpoint}`,
                    err.response?.status || 500,
                    err.response?.data,
                    axios.isCancel(err) || false,
                    err.code === "ECONNABORTED"
                );
            }
            throw new ApiError("Errore di rete", 0);
        }
    }
    get = <T>(endpoint: string, data: Params = {}, headers?: Record<string, string>, timeout?: number): Promise<T> => {
        const query = new URLSearchParams();

        Object.entries(data).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach(v => query.append(key, String(v)));
            } else if (value !== undefined && value !== null) {
                query.append(key, String(value));
            }
        });

        const url = query.toString() ? `${endpoint}?${query.toString()}` : endpoint;

        return this.baseCall(HttpMethod.GET, url, undefined, headers, timeout);
    };

    post = <T>(endpoint: string, data: Params = {}, headers?: Record<string, string>, timeout?: number): Promise<T> =>
        this.baseCall(HttpMethod.POST, endpoint, data, headers, timeout);

    put = <T>(endpoint: string, data: Params = {}, headers?: Record<string, string>, timeout?: number): Promise<T> =>
        this.baseCall(HttpMethod.PUT, endpoint, data, headers, timeout);

    patch = <T>(endpoint: string, data: Params = {}, headers?: Record<string, string>, timeout?: number): Promise<T> =>
        this.baseCall(HttpMethod.PATCH, endpoint, data, headers, timeout);

    delete = <T>(endpoint: string, data: Params = {}, headers?: Record<string, string>, timeout?: number): Promise<T> =>
        this.baseCall(HttpMethod.DELETE, endpoint, data, headers, timeout);
    // Annulla l'ultima richiesta HTTP in corso.
    abortLast = () => {
        this.signalController?.abort();
    };

    protected formatEndpoint(endpoint: string, values: Record<string, string | number>): string {
        for (const [key, value] of Object.entries(values)) endpoint = endpoint.replace(`:${key}`, String(value));
        return endpoint;
    }

    // Formatta i dati di una richiesta GET come stringa di query.
    private formatGetData = (data: Params): string =>
        new URLSearchParams(data).toString() ? `?${new URLSearchParams(data)}` : "";

    // Determina la funzione Axios da usare in base al metodo HTTP richiesto
    private getFunctionByHttpMethod(method: HttpMethod) {
        switch (method) {
            case HttpMethod.GET:
                return this.controller.get.bind(this.controller);
            case HttpMethod.POST:
                return this.controller.post.bind(this.controller);
            case HttpMethod.PUT:
                return this.controller.put.bind(this.controller);
            case HttpMethod.PATCH:
                return this.controller.patch.bind(this.controller);
            case HttpMethod.DELETE:
                return this.controller.delete.bind(this.controller);
            default:
                throw new Error(`Metodo HTTP non supportato: ${method}`);
        }
    }
}
export { ApiError };
export default AbstractCommunicationController;
